import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';

import { Progress } from './ProgressBar';
import { ToastAction } from './Toast';

interface DeletionProgressToastProps {
  message: string;
  objectName: string;
  duration?: number;
  onUndo: () => void;
}

export const DeletionProgressToast: React.FC<DeletionProgressToastProps> = ({
  message,
  objectName,
  duration = 5000,
  onUndo,
}) => {
  const [progress, setProgress] = useState(100);
  const [timeRemaining, setTimeRemaining] = useState(
    Math.ceil(duration / 1000)
  );
  const startTimeRef = useRef<number>(Date.now());
  const intervalIdRef = useRef<Maybe<NodeJS.Timeout>>(null);

  const handleProgressUpdate = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const remaining = Math.max(0, duration - elapsed);
    const newProgress = (remaining / duration) * 100;

    setProgress(newProgress);
    setTimeRemaining(Math.ceil(remaining / 1000));

    if (remaining === 0 && intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, [duration]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    intervalIdRef.current = setInterval(handleProgressUpdate, 100);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [handleProgressUpdate]);

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <Flex direction='col' gap='xs'>
        <Typography variant='body-sm' weight='medium'>
          {message}
        </Typography>
        <Typography variant='body-xs'>
          Deleting {objectName} in {timeRemaining} second
          {timeRemaining !== 1 ? 's' : ''}
        </Typography>
      </Flex>
      <Progress
        value={progress}
        className='h-2 w-full bg-warning-900/20'
        indicatorClassName='bg-warning-900'
      />
      <ToastAction
        altText='Undo deletion'
        onClick={onUndo}
        className='border-neutral-900 text-neutral-900'
      >
        Undo
      </ToastAction>
    </Flex>
  );
};
